import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type {
	ForecastReportAnalytics,
	ForecastReportData,
	ForecastReportMonth,
	ForecastReportYear,
} from "../models/ForecastReport";

const roundTo2 = (v: unknown): number | null => {
	if (v === null || v === undefined || v === "") return null;
	const n = Number(v);
	if (!Number.isFinite(n)) return null;
	return parseFloat(n.toFixed(2));
};

const mapYear = (raw: Record<string, unknown>): ForecastReportYear => ({
	Year: Number(raw.Year),
});

const mapMonth = (raw: Record<string, unknown>): ForecastReportMonth => ({
	Month: String(raw.Month ?? ""),
	ForecastedProduction: roundTo2(raw.ForecastedProduction),
	ActualProduction: roundTo2(raw.ActualProduction),
	Number: Number(raw.Number),
	Name: String(raw.Name ?? ""),
	Year: Number(raw.Year),
});

const mapAnalytics = (
	raw: Record<string, unknown>,
): ForecastReportAnalytics => ({
	ProductionYear: Number(raw.ProductionYear),
	Mean: roundTo2(raw.Mean),
	LastYearActualProduction: roundTo2(raw.LastYearActualProduction),
	Increase: roundTo2(raw.Increase),
	StandardDeviation: roundTo2(raw.StandardDeviation),
	CoefficientOfVariation: roundTo2(raw.CoefficientOfVariation),
	Range: roundTo2(raw.Range),
	PRR: roundTo2(raw.PRR),
});

export interface GetForecastReportParams {
	startYear: number;
	endYear: number;
	signal?: AbortSignal;
}

export const getForecastReport = async ({
	startYear,
	endYear,
	signal,
}: GetForecastReportParams): Promise<ForecastReportData> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "startYear", value: startYear },
		{ name: "endYear", value: endYear },
	];

	const spName = "[SAL].[GetForecastReport]";
	const resp = await apiService.callV2(spName, parameters, signal);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching forecast report");
	}

	const yearsRows = get(resp, "data.tables[0].rows", []) as Record<
		string,
		unknown
	>[];
	const monthlyRows = get(resp, "data.tables[1].rows", []) as Record<
		string,
		unknown
	>[];
	const analyticsRows = get(resp, "data.tables[2].rows", []) as Record<
		string,
		unknown
	>[];

	return {
		years: yearsRows.map(mapYear),
		monthly: monthlyRows.map(mapMonth),
		analytics: analyticsRows.map(mapAnalytics),
	};
};
